/**************************************************************************************
 * Final section in the checkout process. Handles the final steps for the user to place
 * order,
 * *****
 * @format */

import {
    Select,
    Button,
    Icon,
    Alert,
    Checkbox,
    Drawer,
    ModalHeader,
    DrawerFooter,
    DrawerHeader
} from '@sttark/design-system';
import {TermsAndConditions} from 'frontier/cart/components/payments/terms_and_conditions';
import withPaymentsHandler from 'frontier/cart/components/payments/with_payments_handler';
import {formatSingleUserProfiles, formatUserProfiles} from 'frontier/cart/helpers/format';
import {CartCheckoutContext} from 'frontier/cart/components/cart/cart_checkout_context';
import {BillMeDirectly} from 'frontier/cart/components/payments/bill_me_directly';
import {InputWithErrorHandler, SelectWithErrorHandler} from 'frontier-form';
import {METHOD_NET30, METHOD_CREDIT_CARD} from 'frontier-constants';
import React, {useContext, useEffect, useState} from 'react';
import {ErrorDisplay, IfElse, If} from 'frontier-utilities';
import creditCardType from 'credit-card-type';
import {useAcceptJs} from 'react-acceptjs';
import './payment_step.sass';

const authData = {
    apiLoginID: process.env.AUTHORIZE_LOGIN,
    clientKey: process.env.AUTHORIZE_CLIENT_KEY
};

export const PaymentStep = withPaymentsHandler(props => {
    const ctx = useContext(CartCheckoutContext);
    const [showTermsAndConditions, setShowTermsAndConditions] = useState(false);
    const [initialValue, setInitialValue] = useState('');
    const [paymentProfiles, setPaymentProfiles] = useState([]);
    const [canCheckout, setCanCheckout] = useState(false);
    const [showForm, setShowForm] = useState(false);
    const [isNet30, setIsNet30] = useState(null);
    const [saving, setSaving] = useState(false);

    const [cardError, setCardError] = useState(null);

    const environment = process.env.AUTHORIZE_MODE === 'live' ? 'PRODUCTION' : 'SANDBOX';

    const currentYear = new Date().getFullYear();
    const {dispatchData, loading, error} = useAcceptJs({environment, authData});
    const [cvvText, setCvvText] = useState('CVV/Security Code');
    const [cardData, setCardData] = React.useState({
        cardNumber: '',
        month: '',
        year: '',
        cardCode: '',
        cardType: ''
    });

    const handleSubmit = async event => {
        event.preventDefault();
        cardData.cardNumber = cardData.cardNumber.replaceAll(/\D/gm, '');

        // Dispatch CC data to Authorize.net and receive payment nonce for use on your server
        try {
            const response = await dispatchData({cardData});
            props.onSavePaymentProfile({response, cardData});
            setCardData({
                cardNumber: '',
                month: '',
                year: '',
                cardCode: '',
                cardType: ''
            });
        } catch (e) {
            setCardError(e);
        }
    };

    /* payment_id is for CC payments. If a bill me directly is approved them
     * the method is checked to see if bill-me is approved */
    const handlePaymentMethodSelection = (payment_id, method) => {
        setIsNet30(method === METHOD_NET30);

        if (payment_id === 'cta') {
            setShowForm(true);
        } else {
            // they have selected a CC
            if (payment_id) {
                props.onChangeBillingMethod(false, payment_id);
            }
            // else {
            //     // they have de-selected a CC
            //     props.onChangeBillingMethod(true, null);
            // }
        }
    };

    const checkProfileSettings = () => {
        // 1. get the profiles and format them
        const {profiles, initialValue, defaultProfile} = formatUserProfiles(props.paymentProfiles);
        const ppId = ctx.cart.payment_profile_id;

        // 2. set the proper states after formatting
        setInitialValue(initialValue);
        setPaymentProfiles(profiles);

        /* 3. if the cart has a payment_profile_id set they are paying with card
         * update the proper states to reflect this */
        if (ppId) {
            let selectedPaymentProfile = props.paymentProfiles.find(pp => pp.id === ppId);

            if (selectedPaymentProfile) {
                const prof = formatSingleUserProfiles(selectedPaymentProfile);
                setInitialValue(prof);
            }
        }

        // check if the user can checkout
        const payingWithCC = !!(
            ctx.cart.billing_method_id === METHOD_CREDIT_CARD && ctx.cart.payment_profile_id
        );

        const payingWithNet30 =
            ctx.cart.billing_method_id === METHOD_NET30 && !ctx.cart.payment_profile_id;

        setCanCheckout(!saving && ctx.agreedToTerms && (payingWithNet30 || payingWithCC));

        // check if account is net3 30
        setIsNet30(ctx.cart.billing_method_id === METHOD_NET30);
    };

    // update profiles on change
    useEffect(() => {
        checkProfileSettings();
    }, [props]);

    // close form after a successful submission
    useEffect(() => {
        if (props.done) setShowForm(false);
    }, [props.done]);

    useEffect(() => {
        const cardTypeNow = creditCardType(cardData.cardNumber);
        setCvvText(cardTypeNow?.[0]?.code?.name ?? 'CVV/Security Code');
        setCardData({...cardData, cardType: cardTypeNow?.[0]?.niceType});
    }, [cardData.cardNumber]);

    const paymentValidationError = ctx.validationErrors?.payment || undefined;

    return (
        <>
            <TermsAndConditions
                setShowTermsAndConditions={setShowTermsAndConditions}
                isOpen={showTermsAndConditions}
            />
            <div>
                <div className="billing-select-label">
                    <IfElse condition={ctx.cart.billing_method_id > 0}>
                        <h6 className="color-accent-primary m-0">
                            <i className="fa fa-check me-2" />
                            1. Payment method
                        </h6>
                        <span>1. Choose payment method</span>
                    </IfElse>
                </div>

                {/*card*/}
                <div
                    className={`payment-card ${!isNet30 ? 'payment-card--selected' : ''}`}
                    onClick={() => handlePaymentMethodSelection(null, METHOD_CREDIT_CARD)}>
                    <div
                        className={`d-flex align-items-center justify-content-start ${
                            !isNet30 ? 'border-bottom payment-card_header' : ''
                        }`}>
                        <div
                            className={`shadow-input--radio ${
                                !isNet30 ? 'shadow-input-selected' : ''
                            }`}></div>
                        <h6 className="title-container_title font-body-bold-md my-0 ms-3 me-0">
                            Credit/ Debit Card
                        </h6>

                        {/* payment method  cards*/}
                        <div className="payment-methods-available ms-auto me-0 my-0 d-flex align-items-center justify-content-end">
                            <div className="me-2 ms-0 my-0">
                                <Icon name="visa" />
                            </div>
                            <div className="me-2 ms-0 my-0">
                                <Icon name="mastercard" />
                            </div>
                            <div className="me-2 ms-0 my-0">
                                <Icon name="discover" />
                            </div>
                            <div className="mx-0 my-0">
                                <Icon name="amex" />
                            </div>
                        </div>
                    </div>

                    {/* select payment method */}
                    {!isNet30 && (
                        <div className="">
                            <Select
                                placeholder="Select or add a stored payment card"
                                onChange={val =>
                                    handlePaymentMethodSelection(val, METHOD_CREDIT_CARD)
                                }
                                label="*Select or add a stored payment card"
                                options={[...paymentProfiles]}
                                initialValue={initialValue}
                                name="select_address"
                                className="my-0"
                            />
                        </div>
                    )}
                </div>

                {/* bill me directly card */}
                <BillMeDirectly
                    handlePaymentMethodSelection={val => setIsNet30(val)}
                    isOverCreditLimit={ctx.isOverCreditLimit}
                    isOverPastDue={ctx.isOverPastDue}
                    isNet30Approved={props.isNet30Approved}
                    isNet30Selected={isNet30}
                    {...props}
                />

                <div className="d-flex align-items-center justify-content-start agree-to-terms-checkbox">
                    <Checkbox
                        label="Agree to our"
                        onChange={() => setShowTermsAndConditions(true)}
                        checked={ctx.agreedToTerms}
                        value={ctx.agreedToTerms}
                        bold={ctx.agreedToTerms}
                        name="is_default_address"
                        small
                    />
                    <small style={{lineHeight: 1}}>
                        <a
                            style={{
                                fontWeight: 'bold',
                                marginLeft: '8px',
                                fontSize: '18px'
                            }}
                            href="/terms"
                            target="_blank">
                            Terms and Conditions
                        </a>
                    </small>
                </div>
                <div style={{display: 'flex', alignItems: 'center'}}>
                    {!ctx.error && (
                        <div>
                            <Button
                                disabled={ctx.saving || saving}
                                label={saving ? 'Processing Purchase...' : 'Checkout Securely'}
                                onClick={() => {
                                    ctx.onPurchase();
                                    if (canCheckout) setSaving(true);
                                }}
                                className="mt-5 mx-0 mb-7"
                                //disabled={!canCheckout}
                                iconLeft="shield"
                                primary
                            />
                        </div>
                    )}
                    {ctx.saving && (
                        <div className="ml-4">
                            <i className="fa fa-spinner fa-spin" />
                        </div>
                    )}
                </div>

                <If condition={!!paymentValidationError}>
                    <p className="color-alert-negative">{paymentValidationError}</p>
                </If>

                <Drawer onClose={() => setShowForm(false)} isOpen={showForm}>
                    <DrawerHeader
                        title="Save a New Payment Card"
                        onCancel={() => setShowForm(false)}
                        onOk={handleSubmit}
                        okTitle={'Save'}
                        cancelTitle={'Close'}
                    />
                    <section>
                        <div className="add-profile-field form-group w-100 mx-0 mt-0">
                            <InputWithErrorHandler
                                name={'cardNumber'}
                                value={cardData.cardNumber}
                                onChange={value => {
                                    setCardData({...cardData, cardNumber: value});
                                }}
                                errorPath={'account_number'}
                                label="*Card Number"
                                newestds
                            />
                        </div>
                        <div className="add-profile-field form-group w-100 mx-0 mt-0">
                            <InputWithErrorHandler
                                name={'cardCode'}
                                value={cardData.cardCode}
                                onChange={value => setCardData({...cardData, cardCode: value})}
                                label={'*' + cvvText}
                                errorPath={'card_code'}
                                newestds
                            />
                        </div>
                        <div className="d-flex align-items-start justify-content-start mb-0">
                            <div className="add-profile-field form-group mb-0 mr-5">
                                <SelectWithErrorHandler
                                    style={{minWidth: '100px'}}
                                    name={'month'}
                                    options={[...Array(12).keys()].map(number => {
                                        const id = number + 1;
                                        return {id: id.toString(), label: number + 1};
                                    })}
                                    value={cardData.month}
                                    onChange={value => setCardData({...cardData, month: value})}
                                    errorPath={'expires_month'}
                                    placeholder="*Select Month"
                                    label="*Expiration Month"
                                    newestds
                                />
                            </div>

                            <div className="add-profile-field form-group mb-0 ms-3">
                                <SelectWithErrorHandler
                                    style={{minWidth: '100px'}}
                                    name={'year'}
                                    options={[...Array(15).keys()].map(number => {
                                        const optionYear = currentYear + number;
                                        return {id: optionYear.toString(), label: optionYear};
                                    })}
                                    value={cardData.year}
                                    onChange={value => setCardData({...cardData, year: value})}
                                    errorPath={'expires_year'}
                                    placeholder="*Select Year"
                                    label="*Expiration Year"
                                    newestds
                                />
                            </div>
                        </div>
                        {cardError?.messages && (
                            <div>
                                <ul className={'text-alert-negative'}>
                                    {cardError.messages.message.map((message, index) => (
                                        <li key={index}>{message.text}</li>
                                    ))}
                                </ul>
                            </div>
                        )}
                        {props.error && (
                            <div className="text-alert-negative">{props.error?.message}</div>
                        )}
                    </section>
                </Drawer>
            </div>

            {ctx.error && (
                <Alert className="mt-3 mx-0" title="Oops!">
                    <ErrorDisplay error={ctx.error} />
                </Alert>
            )}
        </>
    );
});
